import {
  all,
  delay,
  put,
  takeLatest,
  call,
  select,
  takeEvery,
} from 'redux-saga/effects'

import omit from 'utils/omit'
import ObjectSet from 'utils/ObjectSet'
import deleteFromObjByKeys from 'utils/deleteFromObjByKeys'

import * as actionTypes from './action-types'
import * as actionTypesModal from '../modal/action-types'
import * as actionTypesError from '../errors/action-types'
import { fetchInvitations, removeInvitations, resendInvitation } from './api'
import { invitationsToObjectsItemsInfo, formatSearchOptions } from './business'
import {
  getSelectedIds,
  getInvitationsIds,
  getSearchFilters,
  getCurrentPage,
  getInvitationsList,
} from './selectors'

function* fetchInvitationsSaga({
  payload: { page = 1, filters, keyword = null, removeLastPage = false },
}) {
  try {
    let searchFilters = { keyword: keyword }
    if (!keyword) {
      searchFilters = yield select(getSearchFilters)
    }

    let pageToFetch = page

    if (!pageToFetch) {
      pageToFetch = yield select(getCurrentPage)
    }

    const currentPageResponse = yield call(fetchInvitations, {
      page: pageToFetch,
      keyword: searchFilters.keyword,
      filters,
    })
    const nextPageResponse = yield call(fetchInvitations, {
      page: pageToFetch + 1,
      keyword: searchFilters.keyword,
      filters,
    })
    const {
      _embedded: currentInvitationsList,
      _links,
      ...pagination
    } = currentPageResponse
    const currentInvitations = currentInvitationsList
      ? currentInvitationsList.invitations
      : []

    const { _embedded: nextInvitationsList } = nextPageResponse
    const nextInvitations = nextInvitationsList
      ? nextInvitationsList.invitations
      : []

    const totalInvitations = currentInvitations
      ? invitationsToObjectsItemsInfo([
          ...currentInvitations,
          ...(nextInvitations || []),
        ])
      : {}

    yield put({
      type: actionTypes.SET_INVITATIONS,
      payload: totalInvitations,
    })

    yield put({
      type: actionTypes.SET_PAGINATION,
      payload: {
        ...pagination,
        pages: removeLastPage ? pagination.pages - 1 : pagination.pages,
      },
    })
  } catch (e) {
    yield put({
      type: actionTypes.SET_FETCH_INVITATIONS_ERROR,
      payload: e.message,
    })
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* selectInvitationSaga({ payload: { id } }) {
  const selectedIds = yield select(getSelectedIds)
  const updatedSelected = ObjectSet.toggle(selectedIds, id)
  yield put({
    type: actionTypes.SET_SELECTED,
    payload: updatedSelected,
  })
}

function* selectAllSaga() {
  const vendorsIds = yield select(getInvitationsIds)
  const currentSelected = yield select(getSelectedIds)
  const newSelected = {}
  vendorsIds.slice(0, 10).forEach(vendorId => (newSelected[vendorId] = true))
  yield put({
    type: actionTypes.SET_SELECTED,
    payload: { ...currentSelected, ...newSelected },
  })
}

function* resetSelectedData() {
  const vendorsIds = yield select(getInvitationsIds)
  const currentSelected = yield select(getSelectedIds)
  const newSelected = omit(vendorsIds, currentSelected)
  yield put({
    type: actionTypes.SET_SELECTED,
    payload: { ...newSelected },
  })
}

function* getSearchOptionsSaga({ payload: { keyword } }) {
  try {
    const response = yield call(fetchInvitations, { keyword, limit: 50 })
    const vendors = formatSearchOptions((response._embedded || {}).invitations)

    yield put({ type: actionTypes.SET_SEARCH_OPTIONS, payload: vendors })
  } catch (e) {
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* removeInvitationsSaga({ payload: { ids, filters } }) {
  try {
    yield call(removeInvitations, ids)
    yield put({ type: actionTypes.REMOVE_SUCCESS })
    yield put({
      type: actionTypes.FETCH_INVITATIONS,
      payload: { page: null, filters },
    })
    yield put({ type: actionTypes.RESET_SELECTED })
  } catch (e) {
    yield put({ type: actionTypes.REMOVE_ERROR })
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* fakeRemoveSaga({ payload: { ids, filters } }) {
  const invitations = yield select(getInvitationsList)
  const filteredInvitations = yield call(deleteFromObjByKeys, invitations, ids)
  const currentPage = yield select(getCurrentPage)

  if (!Object.keys(filteredInvitations).length && currentPage !== 1) {
    yield put({
      type: actionTypes.FETCH_INVITATIONS,
      payload: { page: currentPage - 1, filters, removeLastPage: true },
    })
    yield put({ type: actionTypes.RESET_SELECTED })
  } else {
    yield put({
      type: actionTypes.SET_INVITATIONS,
      payload: filteredInvitations,
    })
  }
}

function* confirmResendInvitationSaga({ payload: { id, email } }) {
  try {
    yield call(resendInvitation, { id: id, email: email })
    yield put({ type: actionTypes.RESEND_INVITATION_SUCCESS })
    yield put({ type: actionTypes.RESEND_INVITATION_CONFIRM, payload: true })
    yield delay(300)
    yield put({ type: actionTypesModal.CLOSE_MODAL })
    yield put({ type: actionTypes.RESEND_INVITATION_CLEAR })
    yield put({
      type: actionTypes.FETCH_INVITATIONS,
      payload: { page: 1 },
    })
    yield put({ type: actionTypes.RESET_SELECTED })
  } catch (e) {
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.FETCH_INVITATIONS, fetchInvitationsSaga),
    takeLatest(actionTypes.SELECT_INVITATION, selectInvitationSaga),
    takeLatest(actionTypes.SELECT_ALL, selectAllSaga),
    takeLatest(actionTypes.RESET_SELECTED_ON_PAGE, resetSelectedData),
    takeLatest(actionTypes.GET_SEARCH_OPTIONS, getSearchOptionsSaga),
    takeEvery(actionTypes.REMOVE, removeInvitationsSaga),
    takeEvery(actionTypes.REMOVE_FAKE, fakeRemoveSaga),
    takeEvery(
      actionTypes.CONFIRM_RESEND_INVITATION,
      confirmResendInvitationSaga,
    ),
  ])
}
